import React from "react";
import { Helmet } from "react-helmet-async";
import { generateSeoMetadata, TubeSeoProps } from "../shared/seo";

export interface SeoHeadProps extends TubeSeoProps {
  /**
   * Optional page-specific JSON-LD structured data.
   * Pass a plain JS object (or array of objects) — it will be serialised and
   * injected as a <script type="application/ld+json"> block.
   * Use this for FAQPage, HowTo, BreadcrumbList, etc. that are unique to a page.
   */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export const SeoHead: React.FC<SeoHeadProps> = (props) => {
  const seo = generateSeoMetadata(props);

  // Safely serialise JSON-LD: escape characters that could break inline <script>
  const jsonLdString = props.jsonLd
    ? JSON.stringify(props.jsonLd)
        .replace(/</g, "\\u003c")
        .replace(/>/g, "\\u003e")
        .replace(/&/g, "\\u0026")
    : null;

  return (
    <Helmet>
      {/* Basic HTML Meta Tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={seo.canonicalUrl} />
      {props.noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook / WhatsApp / iMessage */}
      <meta property="og:type" content={props.partNumber ? "product" : "website"} />
      <meta property="og:title" content={seo.ogTitle} />
      <meta property="og:description" content={seo.ogDescription} />
      <meta property="og:image" content={seo.ogImage} />
      <meta property="og:url" content={seo.canonicalUrl} />
      <meta property="og:site_name" content={seo.siteName} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.ogTitle} />
      <meta name="twitter:description" content={seo.ogDescription} />
      <meta name="twitter:image" content={seo.ogImage} />

      {/* Product Price Tag (if applicable) */}
      {props.price && (
        <>
          <meta property="product:price:amount" content={props.price.toString()} />
          <meta property="product:price:currency" content={props.currency || "USD"} />
        </>
      )}

      {/* Page-specific JSON-LD structured data */}
      {jsonLdString && (
        <script type="application/ld+json">{jsonLdString}</script>
      )}
    </Helmet>
  );
};
